import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import type { Dispatch } from 'react';

import type { EditorAction, EditorState } from './editorStore';

import { ApiError, QueryKeys, useUpdateProject } from '@/api';

/**
 * Debounced single-flight autosave (ADR 0010): 800 ms after the last commit,
 * PUT the whole document with the last-known version (BR-20). On success the
 * reducer adopts server codes + version; 409 parks the editor in `conflict`
 * until the user resolves; other failures back off and retry while edits
 * keep accumulating locally.
 */
const DEBOUNCE_MS = 800;
const SUMMARY_INVALIDATE_MIN_MS = 5000;

export const useAutosave = (state: EditorState, dispatch: Dispatch<EditorAction>) => {
  const mutation = useUpdateProject(state.projectId);
  const queryClient = useQueryClient();

  const stateRef = useRef(state);
  const mutateRef = useRef(mutation.mutateAsync);
  useEffect(() => {
    stateRef.current = state;
    mutateRef.current = mutation.mutateAsync;
  });

  const inFlightRef = useRef(false);
  const attemptRef = useRef(0);
  const lastSummaryInvalidateRef = useRef(0);

  const flush = useCallback(async () => {
    const current = stateRef.current;
    if (inFlightRef.current) return;
    if (current.saveStatus === 'conflict') return;
    if (current.revision === current.savedRevision) return;

    inFlightRef.current = true;
    const savedRevision = current.revision;
    dispatch({ type: 'SAVE_STARTED' });
    try {
      const response = await mutateRef.current({
        document: current.document,
        version: current.serverVersion,
      });
      attemptRef.current = 0;
      dispatch({
        type: 'SAVE_SUCCEEDED',
        serverDocument: response.document,
        version: response.version,
        savedRevision,
      });
      const now = Date.now();
      if (now - lastSummaryInvalidateRef.current >= SUMMARY_INVALIDATE_MIN_MS) {
        lastSummaryInvalidateRef.current = now;
        void queryClient.invalidateQueries({
          queryKey: QueryKeys.ProjectSummary(current.projectId),
        });
      }
    } catch (error) {
      if (error instanceof ApiError && error.isConflict) {
        dispatch({ type: 'SAVE_CONFLICT' });
      } else {
        attemptRef.current += 1;
        dispatch({ type: 'SAVE_FAILED' });
      }
    } finally {
      inFlightRef.current = false;
    }
  }, [dispatch, queryClient]);

  useEffect(() => {
    if (state.saveStatus === 'conflict') return undefined;
    if (state.revision === state.savedRevision) return undefined;
    const delay =
      state.saveStatus === 'error'
        ? Math.min(8000, 2000 * 2 ** Math.max(0, attemptRef.current - 1))
        : DEBOUNCE_MS;
    const timer = setTimeout(() => void flush(), delay);
    return () => clearTimeout(timer);
  }, [state.revision, state.savedRevision, state.saveStatus, flush]);

  return { flush };
};
